import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, Tooltip, Typography } from "@mui/material";
import { colors, alphaVar } from "../../../ui/colors";

type CompanyTimeViewProps = {
  companyTime: string;
  companyTimeZone: string;
  companyTimeZoneCity: string;
  localTime: string;
  localTimeZone: string;
  localTimeZoneCity: string;
};

type TimeBlockProps = {
  label: string;
  time: string;
  city: string;
  tooltip: string;
  compact?: boolean;
};

const TimeBlock = ({ label, time, city, tooltip, compact = false }: TimeBlockProps) => (
  <Tooltip title={tooltip} arrow>
    <Box sx={{ minWidth: 0, textAlign: compact ? "right" : "left" }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: colors.gray.base,
            fontSize: "0.62rem",
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: "nowrap",
            textTransform: "none",
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, whiteSpace: "nowrap", mt: 0.35 }}>
          <Typography sx={{ color: colors.gray[400], fontSize: "0.66rem", fontWeight: 600, lineHeight: 1.1 }}>{time}</Typography>
          <LocationOnIcon className="company-time-location-icon" sx={{ color: colors.gray[300], fontSize: 11 }} />
          <Typography sx={{ color: colors.gray[400], fontSize: "0.66rem", fontWeight: 600, lineHeight: 1.1 }}>{city}</Typography>
        </Box>
      </Box>
    </Box>
  </Tooltip>
);

export const CompanyTimeView = ({
  companyTime,
  companyTimeZone,
  companyTimeZoneCity,
  localTime,
  localTimeZone,
  localTimeZoneCity,
}: CompanyTimeViewProps) => (
  <Box
    sx={{
      display: { xs: "none", sm: "flex" },
      alignItems: "center",
      px: 1.25,
      py: 0.8,
      bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)",
      border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
      borderRadius: "8px",
      boxShadow: "none",
      gap: 1,
      transition: "all .3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        borderColor: "#fed7aa",
      },
      "&:hover .company-time-main-icon": {
        color: colors.orange.base,
      },
      "&:hover .company-time-location-icon": {
        color: colors.orange.base,
      },
    }}
  >
    <Box
      sx={{
        width: 18,
        height: 18,
        color: colors.gray[300],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
        transition: "color 0.2s ease",
      }}
      className="company-time-main-icon"
    >
      <AccessTimeFilledIcon sx={{ fontSize: 16 }} />
    </Box>

    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      <TimeBlock
        label="Company time zone"
        time={companyTime}
        city={companyTimeZoneCity}
        tooltip={`Your company uses the ${companyTimeZoneCity} time zone (${companyTimeZone}).`}
      />

      <Box sx={{ width: "1px", height: 24, bgcolor: alphaVar(colors.gray[100], 0.9), flex: "0 0 auto" }} />

      <TimeBlock
        label="Your local time"
        time={localTime}
        city={localTimeZoneCity}
        tooltip={`Your device uses the ${localTimeZoneCity} time zone (${localTimeZone}).`}
        compact
      />
    </Box>
  </Box>
);
